import { FirebaseError } from '@firebase/util';

/**
 * 想定内エラー（バリデーション・業務ルール違反）を表す専用エラークラス #2883
 *
 * ユーザー操作で想定内に発生し、メッセージ表示済みで自己解決可能なエラーに使用する。
 * catch 側でこの型を判別して Sentry 通知をスキップするため、
 * 通信・権限・予期しない例外（想定外エラー）には使用しないこと。
 */
export class ValidationError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'ValidationError';
    // Error を継承した際の prototype チェーンを復元し instanceof を正しく動作させる
    Object.setPrototypeOf(this, ValidationError.prototype);
  }
}

export const throwError = (error: unknown, errorMessage?: string): void => {
  // 想定内エラーは型を保持したまま再スロー（Sentry 判定で instanceof を効かせるため）#2883
  if (error instanceof ValidationError) {
    throw error;
  }
  if (error instanceof Error) {
    throw new Error(error.message);
  } else if (error instanceof FirebaseError) {
    throw new Error(error.code);
  } else if (typeof error === 'string') {
    throw new Error(error);
  } else if (errorMessage) {
    throw new Error(errorMessage || '');
  } else {
    throw new Error();
  }
};

export const common = 'エラーが発生しました。';
export const browserBack = '前のページには戻れません。';
// 管理者を0名にできない旨のメッセージ（ロール変更・削除の各バリデーションで共通利用）
export const adminCountError = '管理者を0名にすることはできません。';

export const authError = {
  tooManyRequests:
    'アカウントが一時的にロックされています。しばらくしてからもう一度お試しください。',
  invalidEmail: '無効なメールアドレスです。',
  userNotFound: 'ユーザーが見つかりません。',
  signInFailed: 'ログインに失敗しました。',
  invalidCredential: 'メールアドレスまたはパスワードに誤りがあります',
  networkRequestFailed:
    'ネットワークエラーが発生しました。インターネット接続を確認し、再度お試しください。',
  userDisabled: 'このアカウントは無効化されています。管理者にお問い合わせください。',
  operationNotAllowed: 'このログイン方法は現在利用できません。管理者にお問い合わせください。',
  // SAML SSO の authDomain が承認済みドメインに登録されていない場合など（#3166）
  unauthorizedDomain:
    'このドメインからの認証は許可されていません。管理者にお問い合わせください。（設定不備の可能性があります）',
};

/**
 * Cloud Functions（callable）のエラーコードに対するユーザー向けメッセージ（#3343）。
 *
 * httpsCallable が投げる FirebaseError の message は 'internal' のような
 * エラーコードそのままで、画面に出しても何も伝わらないため日本語に変換する。
 * キーは code から 'functions/' を除いた部分。
 */
const functionsErrorMessage: Record<string, string> = {
  internal:
    '処理中にエラーが発生しました。時間をおいて再度お試しください。解消しない場合は管理者にお問い合わせください。',
  unavailable: 'サーバーに接続できませんでした。時間をおいて再度お試しください。',
  'deadline-exceeded': '処理に時間がかかり、タイムアウトしました。時間をおいて再度お試しください。',
  unauthenticated: 'ログインの有効期限が切れています。再度ログインしてください。',
  'permission-denied': 'この操作を行う権限がありません。',
  'resource-exhausted': 'アクセスが集中しています。時間をおいて再度お試しください。',
  cancelled: '処理が中断されました。再度お試しください。',
};

/**
 * Firebase Storage のエラーコードに対するユーザー向けメッセージ（#3385）。
 *
 * Storage SDK の例外も FirebaseError だが、message は
 * 'Firebase Storage: User does not have permission...（storage/unauthorized）'
 * のような英文になる。日本語は utils/errorHandler.ts の
 * convertToUserFriendlyMessage と揃えている。
 * キーは code から 'storage/' を除いた部分。
 */
const storageErrorMessage: Record<string, string> = {
  'retry-limit-exceeded': 'ファイルのアップロードに失敗しました。時間をおいて再度お試しください。',
  unknown: 'ファイルのアップロードに失敗しました。時間をおいて再度お試しください。',
  unauthorized: 'ファイルのアップロード権限がありません。再度ログインしてください。',
  unauthenticated: 'ファイルのアップロード権限がありません。再度ログインしてください。',
  canceled: 'ファイルのアップロードがキャンセルされました。',
  'quota-exceeded': 'ストレージの容量上限に達しました。管理者にお問い合わせください。',
  'object-not-found': 'ファイルが見つかりませんでした。再度お試しください。',
};

/** Storage の未知コード用。個別の日本語がない場合に使う。 */
const storageFallbackMessage = 'ファイルの処理中にエラーが発生しました。もう一度お試しください。';

/**
 * 例外からユーザーに見せるメッセージを取り出す（#3343 / #3385）。
 *
 * Cloud Functions のエラーは message がエラーコード（'internal' 等）、
 * Storage / Firestore のエラーは message が英文になる。いずれも FirebaseError
 * なので code から日本語に引き直す（FirestoreError も FirebaseError を継承しており、
 * permission-denied / unavailable などは functionsErrorMessage で拾える）。
 * 業務ロジックが throw した Error はメッセージをそのまま使う。
 *
 * 【サーバが HttpsError に載せたメッセージの扱い（#3420）】
 * invalid-argument / failed-precondition / not-found など変換表にないコードは、
 * サーバが用意した業務メッセージ（「URL は HTTPS で始まる必要があります。」等）を優先する。
 * 一方、変換表にあるコード（internal / permission-denied 等）は「次に何をすればよいか」
 * を伝えるため、サーバの文言より定型文を優先する。
 * サーバの文言を必ず見せたい callable（SAML SSO ログイン: components/pages/AuthLogin.tsx 等）
 * では本関数を使わず error.message を直接表示すること。
 *
 * @param error - catch した例外
 * @param fallback - 変換できなかった場合に使うメッセージ。
 *   呼び出し側で「〜に失敗しました。」と前置きする場合は空文字を渡す。
 */
export const getUserErrorMessage = (error: unknown, fallback: string = common): string => {
  if (error instanceof FirebaseError) {
    // Storage は code の体系が Functions と別なので先に判定する（#3385）
    if (error.code.startsWith('storage/')) {
      return storageErrorMessage[error.code.replace(/^storage\//, '')] ?? storageFallbackMessage;
    }
    const key = error.code.replace(/^functions\//, '');
    const mapped = functionsErrorMessage[key];
    if (mapped) return mapped;
    // サーバが HttpsError に載せた業務メッセージを優先する（#3420）。
    // message 未指定の HttpsError は message がコード名になるため除外する。
    // Firestore のエラー（code に 'functions/' が付かない）は message が英文なので対象外。
    if (error.code.startsWith('functions/') && error.message && error.message !== key) {
      return error.message;
    }
    // 未知のコードは日本語にできないが、完全に隠すと問い合わせ時の切り分けが
    // できなくなるため、定型文にコードを添える。
    return fallback ? `${fallback}（${error.code}）` : error.code;
  }
  if (error instanceof Error) {
    // Functions のエラーが FirebaseError として判定できない場合に備え、
    // message がエラーコードそのままのケースも拾う。
    const mapped = functionsErrorMessage[error.message];
    if (mapped) return mapped;
    return error.message || fallback;
  }
  return fallback;
};
